/**
 * 都市商务模板 - 列表页
 *
 * 设计理念：左栏分类树 + 右栏卡片网格
 * 与 sunset（顶部药丸标签 + 瀑布流）完全不同的排版骨架：
 * - 顶部：页面标题条（面包屑 + 分类名 + 总数）
 * - 主体：左右分栏（左栏分类树固定，右栏卡片网格自适应）
 * - 排序：右栏顶部内嵌（最新/最多浏览/名称）
 * - 列表：整齐的 2-3 列卡片网格 + 加载更多
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/urban/components/Navbar";
import UrbanFooter from "@/templates/urban/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布" },
  { key: "popular", label: "最多浏览" },
  { key: "alpha", label: "名称排序" },
] as const;

export default function UrbanCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  // 加载数据（切换分类/排序时重置）
  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    setCurrentPage(1);
    const params: Record<string, any> = { page: 1, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, sortBy]);

  // 加载更多
  const handleLoadMore = async () => {
    if (loadingMore || listings.length >= total) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const params: Record<string, any> = { page: nextPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    try {
      const data = await getListings(params);
      setListings(prev => [...prev, ...(data.list || [])]);
      setCurrentPage(nextPage);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;
  const hasMore = listings.length < total;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />

      {/* ======== 页面标题条 ======== */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav className="flex items-center gap-1.5 text-sm">
            <Link to="/" className="text-gray-400 hover:text-[#1B3A5C] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            <span className="text-[#1B3A5C] font-semibold">{currentCategoryName}</span>
          </nav>
          <h1 className="text-2xl font-extrabold text-gray-800 mt-2 flex items-center gap-3">
            {currentCategoryName}
            <span className="text-sm font-normal text-gray-400">共 {total} 条信息</span>
          </h1>
        </div>
      </div>

      {/* ======== 主体：左右分栏 ======== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ── 左栏：分类树 ── */}
          <aside className="lg:w-64 shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 p-4 lg:sticky lg:top-24">
              <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#F08A24] rounded-sm inline-block" />
                分类导航
              </h2>
              <button
                onClick={() => setActiveCategoryId("")}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all mb-1 ${
                  !activeCategoryId
                    ? "bg-[#1B3A5C] text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-50 hover:text-[#1B3A5C]"
                }`}
              >
                <i className="ri-apps-2-line text-base" />
                全部
              </button>
              <div className="space-y-1">
                {displayCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id)}
                    className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      activeCategoryId === cat.id
                        ? "bg-[#1B3A5C] text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-50 hover:text-[#1B3A5C]"
                    }`}
                  >
                    <span className="flex items-center gap-2.5 truncate">
                      {cat.icon && <i className={`${cat.icon} text-base opacity-80`} />}
                      <span className="truncate">{cat.name}</span>
                    </span>
                    {activeCategoryId === cat.id && <i className="ri-check-line text-[#F08A24] text-sm shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* ── 右栏：列表区 ── */}
          <div className="flex-1 min-w-0">
            {/* 排序栏 */}
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-gray-400">
                共 <span className="font-semibold text-gray-700">{total}</span> 条信息
              </p>
              <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-100 p-1">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      sortBy === opt.key
                        ? "bg-[#1B3A5C] text-white shadow-sm"
                        : "text-gray-500 hover:text-[#1B3A5C]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 列表 */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <div className="aspect-square bg-gray-200" />
                    <div className="p-4 space-y-2">
                      <div className="w-3/4 h-4 bg-gray-200 rounded" />
                      <div className="w-full h-3 bg-gray-100 rounded" />
                      <div className="w-1/2 h-3 bg-gray-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sortedListings.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {sortedListings.map((listing) => (
                    <Link
                      key={listing.id}
                      to={`/listing/${listing.id}`}
                      className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-[#1B3A5C]/20 hover:-translate-y-0.5 transition-all duration-300"
                    >
                      {/* 缩略图 */}
                      <div className="aspect-square overflow-hidden relative bg-[#1B3A5C]/5">
                        {listing.images?.[0] ? (
                          <img
                            src={listing.images[0]}
                            alt={listing.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <i className="ri-image-line text-[#1B3A5C]/20 text-3xl" />
                            <span className="text-[10px] text-[#1B3A5C]/30">暂无图片</span>
                          </div>
                        )}
                        {listing.is_pinned && (
                          <span className="absolute top-3 left-3 text-[10px] px-2 py-0.5 rounded bg-[#F08A24] text-white font-bold shadow-sm">
                            顶
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-[#1B3A5C] transition-colors">
                          {listing.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                          {listing.content?.substring(0, 80) || "暂无描述"}
                        </p>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                          <span className="text-[11px] px-2 py-0.5 bg-[#1B3A5C]/5 text-[#1B3A5C] rounded font-medium">
                            {listing.category?.name || "未分类"}
                          </span>
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            {sortBy === "popular" ? (
                              <><i className="ri-eye-line" />{listing.view_count}</>
                            ) : (
                              <><i className="ri-time-line" />{formatDate(listing.created_at)}</>
                            )}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* 加载更多 */}
                {hasMore && (
                  <div className="text-center mt-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="inline-flex items-center gap-2 px-10 py-3.5 bg-white border-2 border-[#1B3A5C]/20 text-[#1B3A5C] font-semibold rounded-xl hover:bg-[#1B3A5C] hover:text-white transition-all disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <>
                          <div className="w-5 h-5 border-2 border-[#1B3A5C] border-t-transparent rounded-full animate-spin" />
                          加载中...
                        </>
                      ) : (
                        <>
                          加载更多 <i className="ri-arrow-down-line" />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      已显示 {listings.length} / {total} 条
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-xl bg-[#1B3A5C]/5">
                  <i className="ri-inbox-line text-2xl text-[#1B3A5C]/40" />
                </div>
                <p className="text-gray-500 font-medium">该分类暂无信息</p>
                <p className="text-gray-400 text-sm mt-1">换个分类试试，或返回首页</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white text-sm font-bold rounded-xl hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-md"
                >
                  <i className="ri-arrow-left-line" />返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <UrbanFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
